import { useState } from "react";
import { Crown, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { masterApi, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { ForgotOtpForm } from "@/components/ForgotOtpForm";
import { PasskeySignInButton } from "@/components/PasskeyManager";
import { queuePasskeyOffer } from "@/lib/passkey";

const cls = "h-12 bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#6B21A8]";

const Denied = ({ onBack }) => (
  <div data-testid="admin-login-denied" className="text-center space-y-3">
    <span className="w-14 h-14 mx-auto rounded-2xl bg-red-50 text-red-500 flex items-center justify-center"><ShieldAlert className="w-7 h-7" /></span>
    <p className="text-lg font-extrabold text-slate-900">Access denied</p>
    <p className="text-sm text-slate-500">This account is not a ZapBasket platform admin. Store owners should use the store login.</p>
    <Button data-testid="admin-login-try-again" onClick={onBack} variant="outline" className="w-full h-11 font-bold border-[#6B21A8]/30 text-[#6B21A8] hover:bg-[#6B21A8]/5">Try another account</Button>
  </div>
);

export default function AdminLogin() {
  const { loginMaster, enterPin, logout } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim() || !f.password) return toast.error("Enter admin email and password");
    setBusy(true);
    try {
      const { data } = await masterApi.post("/auth/login", { email: f.email.trim(), password: f.password });
      if (!data.token) throw new Error("This account must finish verification in the store app first");
      localStorage.setItem("zc_master_token", data.token);
      const { data: s } = await masterApi.post("/auth/enter");
      if (!s.user?.is_platform_admin) { logout(); setDenied(true); return; }
      loginMaster(data.token, data.account);
      queuePasskeyOffer();
      enterPin(s.token, s.user);
      toast.success("Welcome to the Super Admin portal");
    } catch (err) { logout(); toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-5" data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-6 shadow-xl shadow-slate-300/40 fade-up">
        <div className="flex items-center gap-2.5 mb-6">
          <span className="w-11 h-11 rounded-xl bg-[#6B21A8]/10 text-[#D97706] flex items-center justify-center"><Crown className="w-6 h-6" /></span>
          <div><p className="text-slate-900 font-extrabold font-head leading-tight">ZapBasket Super Admin</p><p className="text-slate-500 text-xs">Platform owner access only</p></div>
        </div>
        {forgot ? (
          <div data-testid="admin-forgot-panel">
            <ForgotOtpForm initialEmail={f.email} emailPlaceholder="Admin email" accent="bg-[#6B21A8] hover:bg-[#581C87]" onDone={(email) => { setF({ email, password: "" }); setForgot(false); }} onBack={() => setForgot(false)} />
          </div>
        ) : denied ? <Denied onBack={() => { setDenied(false); setF({ email: "", password: "" }); }} /> : (
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="admin-login-email" type="email" autoComplete="username" placeholder="Admin email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={cls} />
            <PasswordInput data-testid="admin-login-password" autoComplete="current-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={cls} />
            <Button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full h-12 bg-[#6B21A8] hover:bg-[#581C87] text-white font-bold shadow-md shadow-[#6B21A8]/25 transition-colors">{busy ? "Signing in…" : "Sign in to Admin"}</Button>
            <PasskeySignInButton auto testid="admin-passkey-signin-btn" className="border-[#6B21A8]/30 text-[#6B21A8] hover:bg-[#6B21A8]/5" onDone={(d) => {
              if (!d.user?.is_platform_admin) { logout(); setDenied(true); return; }
              loginMaster(d.master_token, d.account); enterPin(d.token, d.user); toast.success("Welcome to the Super Admin portal");
            }} />
            <button data-testid="admin-forgot-link" type="button" onClick={() => setForgot(true)} className="w-full text-center text-sm font-semibold text-[#D97706] hover:text-[#B45309] pt-1">Forgot Password / Reset Password</button>
          </form>
        )}
      </div>
    </div>
  );
}
